import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import SchoolIcon from "@mui/icons-material/School";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VideocamIcon from "@mui/icons-material/Videocam";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {useNavigate} from "react-router";
import {
    getComputedTrainingStatus,
    isDeadlinePassed,
    getDaysUntilDeadline,
} from "./trainingValidation";

export const InstructorTrainingCard = ({training}) => {
    const navigate = useNavigate();

    if (!training) return null;

    const statusConfig = getComputedTrainingStatus(training);

    const deadlinePassed = isDeadlinePassed(training.registrationDeadline);
    const daysLeft = getDaysUntilDeadline(training.registrationDeadline);

    const isDigital = training.format === "digital";
    const mailtoHref = training.registrationEmail
        ? `mailto:${training.registrationEmail}?subject=Påmelding: ${encodeURIComponent(training.title)}`
        : null;

    const infoHref = training.infoUrl || "/get-educated";

    return (
        <Card
            elevation={0}
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                border: "1px solid",
                borderColor: "rgba(40, 82, 56, 0.14)",
                bgcolor: "background.paper",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.04)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                    boxShadow: "0 8px 24px rgba(40, 82, 56, 0.08)",
                    borderColor: "primary.main",
                },
            }}
        >
            <CardContent sx={{p: {xs: 2.5, sm: 3}, flex: "1 0 auto"}}>
                {/* Badges row */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <Chip
                        icon={isDigital ? <VideocamIcon /> : <LocationOnIcon />}
                        label={isDigital ? "Digital opplæring (Zoom)" : training.format === "hybrid" ? "Hybrid" : "Fysisk samling"}
                        size="small"
                        sx={{
                            fontWeight: 700,
                            bgcolor: isDigital ? "rgba(33, 150, 243, 0.08)" : "rgba(40, 82, 56, 0.08)",
                            color: isDigital ? "#1976d2" : "primary.main",
                            borderRadius: 1.5,
                        }}
                    />

                    <Chip
                        label={statusConfig.label}
                        color={statusConfig.color}
                        size="small"
                        sx={{fontWeight: 700, borderRadius: 1.5}}
                    />
                </Box>

                {/* Title and Subtitle */}
                <Typography
                    variant="h5"
                    component="h3"
                    sx={{fontWeight: 800, color: "text.primary", mb: 0.5, lineHeight: 1.3}}
                >
                    {training.title}
                </Typography>

                {training.subtitle && (
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        {training.subtitle}
                    </Typography>
                )}

                {/* Key metadata list */}
                <Stack spacing={1.25} sx={{my: 2.5}}>
                    <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                        <PersonIcon fontSize="small" sx={{color: "primary.main"}} />
                        <Typography variant="body2" sx={{fontWeight: 600}}>
                            Kursholder: {training.instructorName}
                        </Typography>
                    </Box>

                    {training.scheduleSummary && (
                        <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                            <AccessTimeIcon fontSize="small" sx={{color: "primary.main"}} />
                            <Typography variant="body2" color="text.secondary">
                                Tidspunkt: {training.scheduleSummary}
                            </Typography>
                        </Box>
                    )}

                    {training.price && (
                        <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                            <SchoolIcon fontSize="small" sx={{color: "primary.main"}} />
                            <Typography variant="body2" color="text.secondary">
                                Kursavgift: {training.price}
                            </Typography>
                        </Box>
                    )}
                </Stack>

                <Divider sx={{my: 2}} />

                {/* Sessions list */}
                {Array.isArray(training.sessions) && training.sessions.length > 0 && (
                    <Box sx={{mb: 2.5}}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 700,
                                textTransform: "uppercase",
                                fontSize: "0.75rem",
                                letterSpacing: "0.05em",
                                color: "text.secondary",
                                mb: 1.5,
                            }}
                        >
                            Samlinger:
                        </Typography>
                        <Stack spacing={1}>
                            {training.sessions.map((s, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        p: 1.25,
                                        borderRadius: 2,
                                        bgcolor: "rgba(40, 82, 56, 0.04)",
                                        border: "1px solid rgba(40, 82, 56, 0.08)",
                                    }}
                                >
                                    <Box sx={{display: "flex", alignItems: "baseline", gap: 1}}>
                                        <Typography variant="body2" sx={{fontWeight: 700, color: "primary.main"}}>
                                            {s.sessionNumber}. samling:
                                        </Typography>
                                        <Typography variant="body2" sx={{fontWeight: 600}}>
                                            {s.dates}
                                        </Typography>
                                    </Box>
                                    {s.notes && (
                                        <Typography variant="caption" color="text.secondary" sx={{display: "block", mt: 0.25}}>
                                            {s.notes}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}

                {/* Registration Deadline Warning */}
                {training.registrationDeadline && (
                    <Box
                        sx={{
                            p: 1.25,
                            borderRadius: 2,
                            bgcolor: deadlinePassed ? "rgba(211, 47, 47, 0.08)" : "rgba(237, 108, 2, 0.08)",
                            border: "1px solid",
                            borderColor: deadlinePassed ? "rgba(211, 47, 47, 0.2)" : "rgba(237, 108, 2, 0.2)",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        <WarningAmberIcon
                            fontSize="small"
                            sx={{color: deadlinePassed ? "error.main" : "warning.main"}}
                        />
                        <Typography variant="caption" sx={{fontWeight: 600}}>
                            {deadlinePassed
                                ? `Påmeldingsfristen utløp ${training.registrationDeadline}`
                                : `Påmeldingsfrist: ${training.registrationDeadline} (${daysLeft} dager igjen)`}
                        </Typography>
                    </Box>
                )}
            </CardContent>

            {/* Actions footer */}
            <Box
                sx={{
                    p: {xs: 2, sm: 2.5},
                    pt: 0,
                    display: "flex",
                    flexDirection: {xs: "column", sm: "row"},
                    gap: 1.5,
                }}
            >
                {mailtoHref && (
                    <Button
                        component="a"
                        href={statusConfig.isBookable ? mailtoHref : undefined}
                        variant="contained"
                        color="primary"
                        startIcon={<EmailIcon />}
                        disabled={!statusConfig.isBookable}
                        sx={{
                            flex: 1,
                            minHeight: 44,
                            fontWeight: 700,
                            textTransform: "none",
                            borderRadius: 2,
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        {statusConfig.isClosed ? "Påmelding stengt" : "Meld på via e-post"}
                    </Button>
                )}

                <Button
                    onClick={() => {
                        if (infoHref.startsWith("http")) {
                            window.open(infoHref, "_blank", "noopener,noreferrer");
                        } else {
                            navigate(infoHref);
                        }
                    }}
                    variant="outlined"
                    color="primary"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                        flex: 1,
                        minHeight: 44,
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 2,
                        "&:focus-visible": {
                            outline: "2px solid #285238",
                            outlineOffset: "2px",
                        },
                    }}
                >
                    Les mer
                </Button>
            </Box>
        </Card>
    );
};
